<template>
  <div class="clip">
    <video
      ref="videoRef"
      class="clip__video"
      :src="src"
      :poster="poster"
      :autoplay="!reducedMotion"
      :controls="reducedMotion"
      muted
      loop
      playsinline
      preload="auto"
      :aria-label="label"
      @loadedmetadata="onReady"
      @error="$emit('error')"
    />

    <!-- Playback controls and the progress read-out, on the clip's bottom edge. All of it is
         suppressed under reduced motion, where the native controls do the same job. -->
    <template v-if="!reducedMotion">
      <div v-if="!hideControls" class="clip__tools">
        <button type="button" class="clip__tool" :aria-label="t('onboarding.video.back')" @click="seek(-5)">
          <v-icon icon="mdi-rewind-5" size="22" />
        </button>
        <button type="button" class="clip__tool" :aria-label="t('onboarding.video.forward')" @click="seek(5)">
          <v-icon icon="mdi-fast-forward-5" size="22" />
        </button>
        <button
          type="button"
          class="clip__tool clip__tool--rate"
          :class="{ 'clip__tool--on': rate !== 1 }"
          :aria-label="t('onboarding.video.speed')"
          :aria-pressed="rate !== 1"
          @click="toggleRate"
        >
          {{ rate }}×
        </button>
      </div>
      <span v-if="duration" class="clip__time">{{ clock(elapsed) }} / {{ clock(duration) }}</span>
      <div class="clip__bar">
        <div class="clip__bar-fill" :style="{ transform: `scaleX(${progress})` }" />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * @fileOverview One clip of the onboarding walkthrough, with its progress bar and controls.
 *
 * Its own component because the closing screen plays a clip too, and it wants the same read-out and
 * the same buttons — the alternative was a second copy of the element, the bar, the three buttons and
 * the `requestAnimationFrame` loop that drives them.
 *
 * The chosen speed lives in the parent (`v-model:rate`) so it survives this component being rebuilt,
 * which happens on every step: the element is re-keyed on purpose, because swapping only `src` leaves
 * the previous frame on screen until the new file decodes and reads as the wrong clip being shown.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"

const props = defineProps<{
  src: string
  poster: string
  label: string
  /** Under `prefers-reduced-motion` the clip holds on its poster with native controls instead. */
  reducedMotion: boolean
  rate: number
  /**
   * Drops the seek and speed buttons, leaving the progress bar and the read-out. For a clip nobody
   * is meant to drive — the closing screen's, which is a nine-second aside, not a lesson. The
   * inherited {@link rate} still applies; it just cannot be changed from there.
   */
  hideControls?: boolean
}>()

const emit = defineEmits<{ error: []; "update:rate": [value: number] }>()

const { t } = useI18n()

const videoRef = ref<HTMLVideoElement | null>(null)
const elapsed = ref(0)
const duration = ref(0)

const progress = computed(() => (duration.value > 0 ? Math.min(1, elapsed.value / duration.value) : 0))

let frame = 0

/**
 * Drives the progress bar from `requestAnimationFrame` rather than from `timeupdate`, which browsers
 * fire about four times a second — enough for a read-out, visibly steppy for a bar.
 */
const track = () => {
  const element = videoRef.value
  if (element) {
    elapsed.value = element.currentTime
    if (Number.isFinite(element.duration)) duration.value = element.duration
  }
  frame = requestAnimationFrame(track)
}

/**
 * Autoplay is a request, not a guarantee — a muted inline video is allowed everywhere that matters,
 * but a browser that declines would leave the clip on its poster with no way to start it. Asking
 * again explicitly costs nothing and covers that.
 */
const onReady = () => {
  const element = videoRef.value
  if (!element) return
  if (Number.isFinite(element.duration)) duration.value = element.duration
  // Re-applied here because `playbackRate` is a property of the element, and the element is new.
  element.playbackRate = props.rate
  if (!props.reducedMotion) element.play().catch(() => {})
}

/**
 * Jumps `delta` seconds, clamped inside the clip.
 *
 * The upper clamp stops just short of the end rather than at it, so a forward jump near the end lands
 * on the closing frame and is *seen* before the loop takes it back to the start. Setting
 * `currentTime` to `duration` exactly skips that: the loop fires immediately and the jump is
 * indistinguishable from the button doing nothing.
 */
const seek = (delta: number) => {
  const element = videoRef.value
  if (!element || !Number.isFinite(element.duration)) return
  element.currentTime = Math.min(element.duration - 0.4, Math.max(0, element.currentTime + delta))
  elapsed.value = element.currentTime
}

/** Two speeds, not a menu: the only thing anyone wants here is "get on with it". */
const toggleRate = () => {
  const next = props.rate === 1 ? 2 : 1
  if (videoRef.value) videoRef.value.playbackRate = next
  emit("update:rate", next)
}

/** m:ss, for the read-out beside the bar. */
const clock = (seconds: number) => {
  const total = Math.max(0, Math.round(seconds))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`
}

onMounted(() => {
  frame = requestAnimationFrame(track)
})

onBeforeUnmount(() => {
  if (frame) cancelAnimationFrame(frame)
  frame = 0
})
</script>

<style scoped>
.clip {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  /* Reserve the frame before the file arrives, so the card does not jump when it does. */
  aspect-ratio: 16 / 9;
  background: rgba(var(--v-theme-on-surface), 0.06);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.clip__video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Bottom-left of the clip, opposite the time read-out. Quiet until hovered — they are for the
   minority who want to hurry a clip along, not decoration on every frame — but sized as real targets:
   the first pass was a 30x24 hit area, which is below what a finger can reliably hit and read as an
   afterthought even with a mouse. */
.clip__tools {
  position: absolute;
  left: 10px;
  bottom: 12px;
  display: flex;
  gap: 6px;
}

.clip__tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  height: 32px;
  padding: 0 9px;
  border-radius: 7px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  transition: background 0.15s ease;
}

.clip__tool:hover,
.clip__tool:focus-visible {
  background: rgba(0, 0, 0, 0.78);
}

/* Not the accent, which the progress bar already owns — a second gold thing on the same edge would
   read as related to it. */
.clip__tool--on {
  background: rgb(var(--v-theme-primary));
}

.clip__time {
  position: absolute;
  right: 10px;
  bottom: 12px;
  padding: 4px 9px;
  border-radius: 7px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
  pointer-events: none;
}

.clip__bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 5px;
  background: rgba(0, 0, 0, 0.45);
}

/* The accent, not the primary: this bar lies over a screen recording of the app, which is mostly
   the primary indigo already — the brand blue on brand blue was invisible. Gold is the palette's
   emphasis colour and the only one that reads against any frame of any clip, in either theme.
   `transform: scaleX` rather than `width`, so the sixty-times-a-second update stays on the
   compositor and never asks the card's layout to be recalculated. */
.clip__bar-fill {
  height: 100%;
  background: rgb(var(--v-theme-accent));
  transform-origin: left center;
  will-change: transform;
}
</style>
